import { normalizeFilters } from '@user/components/Filters'
import { defaultOrderBy, ILead, LeadFilterSet } from '@user/store/lead'
import { produce } from 'immer'
import {
  isValidEmail,
  isValidPhone,
  updatePhoneValueSearch,
} from '@sales-center/common'
import { IOpportunity, OpportunityFilterSet } from '@user/store/opportunity'

export const defaultGuestUserLeadFilters = normalizeFilters<
  ILead,
  LeadFilterSet
>({
  appliedCount: 0,
  normalizedFilters: [],
  orderBy: defaultOrderBy,
  filters: {
    createdDate: {
      propertyName: 'createdDate',
      type: 'dateRange',
      value: { matchType: 'bt', start: null, end: null },
    },

    disqualifiedDate: {
      propertyName: 'disqualifiedDate',
      type: 'dateRange',
      value: { matchType: 'bt', start: null, end: null },
    },
    nextActionDate: {
      propertyName: 'nextActionDate',
      type: 'dateRange',
      value: { matchType: 'bt', start: null, end: null },
    },
    createdBy: {
      propertyName: 'createdBy',
      type: 'multiSelect',
      nestedPropertyName: 'id',
      propertyIsArray: false,
      propertyIsString: false,
      value: { mode: 'any', options: [] },
    },
    submittedBy: {
      propertyName: 'submittedBy',
      type: 'multiSelect',
      propertyIsArray: false,
      propertyIsString: true,
      value: { mode: 'any', options: [] },
    },
    assignee: {
      propertyName: 'assignee',
      type: 'multiSelect',
      nestedPropertyName: 'id',
      propertyIsArray: false,
      propertyIsString: false,
      value: { mode: 'any', options: [] },
    },
    services: {
      propertyName: 'services',
      type: 'multiSelect',
      nestedPropertyName: 'id',
      propertyIsArray: true,
      propertyIsString: true,
      value: { mode: 'any', options: [] },
    },
    activityStatus: {
      propertyName: 'activities',
      type: 'multiSelect',
      nestedPropertyName: 'status',
      propertyIsArray: true,
      propertyIsString: true,
      value: { mode: 'any', options: [] },
    },
    activityTypeId: {
      propertyName: 'activities',
      type: 'multiSelect',
      nestedPropertyName: 'ActivityTypeId',
      propertyIsArray: true,
      propertyIsString: false,
      value: { mode: 'any', options: [] },
    },
    salesFunnelId: {
      propertyName: 'salesFunnelId',
      type: 'multiSelect',
      propertyIsArray: false,
      propertyIsString: false,
      value: { mode: 'any', options: [] },
    },
    salesFunnelStageId: {
      propertyName: 'salesFunnelStageId',
      type: 'multiSelect',
      propertyIsArray: false,
      propertyIsString: false,
      value: { mode: 'any', options: [] },
    },
    initialValue: {
      propertyName: 'initialValue',
      type: 'valueRange',
      value: { min: '', max: '' },
    },
    recurringValue: {
      propertyName: 'recurringValue',
      type: 'valueRange',
      value: { min: '', max: '' },
    },
    totalLeadValue: {
      propertyName: 'totalLeadValue',
      type: 'valueRange',
      value: { min: '', max: '' },
    },
    locationTypeId: {
      propertyName: 'locationTypeId',
      type: 'multiSelect',
      propertyIsArray: false,
      propertyIsString: true,
      value: { mode: 'any', options: [] },
    },
    postalCode: {
      propertyName: 'postalCode',
      type: 'multiSelect',
      propertyIsArray: false,
      propertyIsString: true,
      startsWith: true,
      value: { mode: 'any', options: [] },
    },
    billToPostalCode: {
      propertyName: 'billtoPostalCode',
      type: 'multiSelect',
      propertyIsArray: false,
      propertyIsString: true,
      startsWith: true,
      value: { mode: 'any', options: [] },
    },
    divisionId: {
      propertyName: 'divisionId',
      type: 'multiSelect',
      propertyIsArray: false,
      propertyIsString: true,
      value: { mode: 'any', options: [] },
    },
    closed: {
      propertyName: 'closedDate',
      type: 'nullable',
      value: 'empty',
    },
    salesTeamId: {
      propertyName: 'salesTeamId',
      type: 'multiSelect',
      propertyIsArray: false,
      propertyIsString: false,
      value: { mode: 'any', options: [] },
    },
    salesTeamIds: {
      propertyName: 'salesTeamId',
      type: 'multiSelect',
      propertyIsArray: false,
      propertyIsString: false,
      value: { mode: 'any', options: [] },
    },
    sourceId: {
      propertyName: 'sourceId',
      type: 'multiSelect',
      propertyIsArray: false,
      propertyIsString: true,
      value: { mode: 'any', options: [] },
    },
    campaignId: {
      propertyName: 'campaignId',
      type: 'multiSelect',
      propertyIsArray: false,
      propertyIsString: true,
      value: { mode: 'any', options: [] },
    },
    type: {
      propertyName: 'type',
      type: 'multiSelect',
      propertyIsArray: false,
      propertyIsString: true,
      value: { mode: 'any', options: [] },
    },
    importId: {
      propertyName: 'importId',
      type: 'multiSelect',
      propertyIsArray: false,
      propertyIsString: false,
      value: { mode: 'any', options: [] },
    },
    targetPests: {
      propertyName: 'targetPests',
      type: 'multiSelect',
      nestedPropertyName: 'id',
      propertyIsArray: true,
      propertyIsString: true,
      value: { mode: 'any', options: [] },
    },
    agreementStatuses: {
      propertyName: 'agreementStatuses',
      type: 'multiSelect',
      nestedPropertyName: 'id',
      propertyIsArray: true,
      propertyIsString: true,
      hideAllMode: true,
      value: { mode: 'any', options: [] },
    },
    hasPestPacLocation: {
      propertyName: 'hasPestPacLocation',
      type: 'ternary',
      value: 'empty',
    },
    knockStatusSettingId: {
      propertyName: 'knockStatusSettingId',
      type: 'multiSelect',
      propertyIsArray: false,
      propertyIsString: false,
      value: { mode: 'any', options: [] },
    },
  },
  search: {
    value: '',
    placeholder: 'Name, email, phone, address, location',
    searchFields: [
      {
        propertyNames: [
          'contactName',
          'billToContactName',
          'company',
          'primaryContactSearchName',
        ],
        matchType: 'contains',
      },
      {
        propertyNames: ['externalLocationCode'],
        matchType: 'eq',
        include: /^[\d]+$/,
      },
      {
        propertyNames: [
          'phone',
          'mobilePhone',
          'billToPhone',
          'billToMobilePhone',
          'primaryContactSearchPhone',
          'primaryContactSearchMobile',
        ],
        matchType: 'eq',
        include: isValidPhone,
        formatter: updatePhoneValueSearch,
      },
      {
        propertyNames: [
          'emailAddress',
          'billToEmail',
          'primaryContactSearchPrimaryEmail',
        ],
        matchType: 'eq',
        include: isValidEmail,
      },
      {
        propertyNames: [
          'address',
          'billToAddress',
          'primaryContactSearchAddress',
        ],
        matchType: 'contains',
      },
    ],
  },
})

export const guestUserClearedLeadFilters = produce(
  defaultGuestUserLeadFilters,
  (_draft) => {},
)

export const defaultGuestUserOpportunityFilters = normalizeFilters<
  IOpportunity,
  OpportunityFilterSet
>({
  appliedCount: 0,
  normalizedFilters: [],
  orderBy: 'createdDate desc,id desc',
  filters: {
    createdDate: {
      propertyName: 'createdDate',
      type: 'dateRange',
      value: { matchType: 'bt', start: null, end: null },
    },
    leadCreatedTime: {
      propertyName: 'leadCreatedTime',
      type: 'dateRange',
      value: { matchType: 'bt', start: null, end: null },
    },
    closedDate: {
      propertyName: 'closedDate',
      type: 'dateRange',
      value: { matchType: 'bt', start: null, end: null },
    },
    nextActionDate: {
      propertyName: 'nextActionDate',
      type: 'dateRange',
      value: { matchType: 'bt', start: null, end: null },
    },
    expectedClosingDate: {
      propertyName: 'expectedClosingDate',
      type: 'dateRange',
      value: { matchType: 'bt', start: null, end: null },
    },
    services: {
      propertyName: 'services',
      type: 'multiSelect',
      propertyIsArray: true,
      propertyIsString: true,
      nestedPropertyName: 'id',
      value: { mode: 'any', options: [] },
    },
    activityStatus: {
      propertyName: 'activities',
      type: 'multiSelect',
      nestedPropertyName: 'status',
      propertyIsArray: true,
      propertyIsString: true,
      value: { mode: 'any', options: [] },
    },
    activityTypeId: {
      propertyName: 'activities',
      type: 'multiSelect',
      nestedPropertyName: 'ActivityTypeId',
      propertyIsArray: true,
      propertyIsString: false,
      value: { mode: 'any', options: [] },
    },
    salesFunnelId: {
      propertyName: 'salesFunnelId',
      type: 'multiSelect',
      propertyIsArray: false,
      propertyIsString: false,
      value: { mode: 'any', options: [] },
    },
    salesFunnelStageId: {
      propertyName: 'salesFunnelStageId',
      type: 'multiSelect',
      propertyIsArray: false,
      propertyIsString: false,
      value: { mode: 'any', options: [] },
    },
    initialValue: {
      propertyName: 'initialValue',
      type: 'valueRange',
      value: { min: '', max: '' },
    },
    recurringValue: {
      propertyName: 'recurringValue',
      type: 'valueRange',
      value: { min: '', max: '' },
    },
    totalValue: {
      propertyName: 'totalValue',
      type: 'valueRange',
      value: { min: '', max: '' },
    },
    createdBy: {
      propertyName: 'createdBy',
      type: 'multiSelect',
      propertyIsArray: false,
      propertyIsString: false,
      nestedPropertyName: 'id',
      value: { mode: 'any', options: [] },
    },
    submittedBy: {
      propertyName: 'submittedBy',
      type: 'multiSelect',
      propertyIsArray: false,
      propertyIsString: true,
      value: { mode: 'any', options: [] },
    },
    assignee: {
      propertyName: 'assignee',
      type: 'multiSelect',
      propertyIsArray: false,
      propertyIsString: false,
      nestedPropertyName: 'id',
      value: { mode: 'any', options: [] },
    },
    postalCode: {
      propertyName: 'postalCode',
      type: 'multiSelect',
      propertyIsArray: false,
      propertyIsString: true,
      startsWith: true,
      value: { mode: 'any', options: [] },
    },
    billToPostalCode: {
      propertyName: 'billToPostalCode',
      type: 'multiSelect',
      propertyIsArray: false,
      propertyIsString: true,
      startsWith: true,
      value: { mode: 'any', options: [] },
    },
    locationTypeId: {
      propertyName: 'locationTypeId',
      type: 'multiSelect',
      propertyIsArray: false,
      propertyIsString: true,
      value: { mode: 'any', options: [] },
    },
    divisionId: {
      propertyName: 'divisionId',
      type: 'multiSelect',
      propertyIsArray: false,
      propertyIsString: true,
      value: { mode: 'any', options: [] },
    },
    closed: {
      propertyName: 'closedDate',
      type: 'nullable',
      value: 'empty',
    },
    salesTeamId: {
      propertyName: 'salesTeamId',
      type: 'multiSelect',
      propertyIsArray: false,
      propertyIsString: false,
      value: { mode: 'any', options: [] },
    },
    salesTeamIds: {
      propertyName: 'salesTeamId',
      type: 'multiSelect',
      propertyIsArray: false,
      propertyIsString: false,
      value: { mode: 'any', options: [] },
    },
    sourceId: {
      propertyName: 'sourceId',
      type: 'multiSelect',
      propertyIsArray: false,
      propertyIsString: true,
      value: { mode: 'any', options: [] },
    },
    campaignId: {
      propertyName: 'campaignId',
      type: 'multiSelect',
      propertyIsArray: false,
      propertyIsString: true,
      value: { mode: 'any', options: [] },
    },
    soldServiceStatus: {
      propertyName: 'soldServiceStatus',
      type: 'multiSelect',
      propertyIsArray: false,
      propertyIsString: true,
      value: { mode: 'any', options: [] },
    },
    type: {
      propertyName: 'type',
      type: 'multiSelect',
      propertyIsArray: false,
      propertyIsString: true,
      value: { mode: 'any', options: [] },
    },
    importId: {
      propertyName: 'importId',
      type: 'multiSelect',
      propertyIsArray: false,
      propertyIsString: false,
      value: { mode: 'any', options: [] },
    },
    targetPests: {
      propertyName: 'targetPests',
      type: 'multiSelect',
      nestedPropertyName: 'id',
      propertyIsArray: true,
      propertyIsString: true,
      value: { mode: 'any', options: [] },
    },
    agreementStatuses: {
      propertyName: 'agreementStatuses',
      type: 'multiSelect',
      nestedPropertyName: 'id',
      propertyIsArray: true,
      propertyIsString: true,
      hideAllMode: true,
      value: { mode: 'any', options: [] },
    },
    hasPestPacLocation: {
      propertyName: 'hasPestPacLocation',
      type: 'ternary',
      value: 'empty',
    },
    closedLost: {
      propertyName: 'closedLost',
      type: 'ternary',
      value: 'empty',
    },
    leadCreatedBy: {
      propertyName: 'leadCreatedBy',
      type: 'multiSelect',
      nestedPropertyName: 'id',
      propertyIsArray: false,
      propertyIsString: false,
      value: { mode: 'any', options: [] },
    },
    knockStatusSettingId: {
      propertyName: 'knockStatusSettingId',
      type: 'multiSelect',
      propertyIsArray: false,
      propertyIsString: false,
      value: { mode: 'any', options: [] },
    },
  },
  search: {
    value: '',
    placeholder: 'Name, email, phone, address, location',
    searchFields: [
      {
        propertyNames: ['opportunityName'],
        matchType: 'contains',
      },
      {
        propertyNames: [
          'contactName',
          'billToContactName',
          'company',
          'primaryContactSearchName',
        ],
        matchType: 'contains',
      },
      {
        propertyNames: ['externalLocationCode'],
        matchType: 'eq',
        include: /^[\d]+$/,
      },
      {
        propertyNames: [
          'phone',
          'mobilePhone',
          'billToPhone',
          'billToMobilePhone',
          'primaryContactSearchPhone',
          'primaryContactSearchMobile',
        ],
        matchType: 'eq',
        include: isValidPhone,
        formatter: updatePhoneValueSearch,
      },
      {
        propertyNames: [
          'emailAddress',
          'billToEmail',
          'primaryContactSearchPrimaryEmail',
        ],
        matchType: 'eq',
        include: isValidEmail,
      },
      {
        propertyNames: [
          'address',
          'billToAddress',
          'primaryContactSearchAddress',
        ],
        matchType: 'contains',
      },
    ],
  },
})

export const guestUserClearedOpportunityFilters = produce(
  defaultGuestUserOpportunityFilters,
  (_draft) => {},
)
